import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";

const Footer = () => {
  return (
    <footer
      id="footer"
      className="text-white bg-gradient-to-b from-[#08150A] via-[#0E2011] to-[#071108] border-t border-[#213F24] relative overflow-hidden pt-10 pb-6"
    >
      {/* Top Emerald Accent Glow Bar */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-[2px] bg-gradient-to-r from-transparent via-[#2CB742] to-transparent opacity-80" />

      <div className="max-w-[1700px] mx-auto px-6 lg:px-8">
        {/* COMPACT 4-COLUMN FOOTER GRID */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 border-b border-white/10 pb-8">

          {/* COLUMN 1: COMPANY LOGO & COMPACT PITCH (Col span 4) */}
          <div className="md:col-span-4 space-y-3">
            <img 
              src="/logo.png" 
              alt="ChefCo Hospitality Partners" 
              className="h-14 md:h-16 w-auto object-contain" 
            />
            <p className="text-white/60 leading-relaxed text-xs max-w-sm">
              CHEFCO Hospitality Partners LLP is a premium wholesale procurement platform serving restaurants, hotels, bakeries, cafés, and institutional kitchens.
            </p>
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <span className="inline-flex items-center gap-1 bg-[#152B18] px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide border border-[#2CB742]/30 text-[#2CB742]">
                🌿 Eco Sourcing
              </span>
              <span className="inline-flex items-center gap-1 bg-[#152B18] px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide border border-emerald-400/20 text-emerald-300">
                <ShieldCheck className="w-3 h-3 text-emerald-400" /> Food Safe
              </span>
            </div>
          </div>

          {/* COLUMN 2 & 3: EXPLORE & ACCOUNT (Grouped side-by-side on mobile) */}
          <div className="md:col-span-5 grid grid-cols-2 gap-4">
            {/* EXPLORE LINKS */}
            <div>
              <h4 className="font-black text-xs uppercase tracking-widest mb-3 text-[#2CB742]">Explore Catalog</h4>
              <ul className="space-y-2 text-white/70 text-xs font-semibold m-0 p-0 list-none">
                <li className="hover:text-[#2CB742] hover:translate-x-1 transition-all duration-200">
                  <Link to="/departments" className="text-inherit no-underline block">Shop by Department</Link>
                </li>
                <li className="hover:text-[#2CB742] hover:translate-x-1 transition-all duration-200">
                  <Link to="/" className="text-inherit no-underline block">Wholesale Products</Link>
                </li>
                <li className="hover:text-[#2CB742] hover:translate-x-1 transition-all duration-200">
                  <Link to="/quickorder" className="text-inherit no-underline block">Quick Order</Link>
                </li>
                <li className="hover:text-[#2CB742] hover:translate-x-1 transition-all duration-200">
                  <Link to="/flyerdeals" className="text-inherit no-underline block">Flyer Deals</Link>
                </li>
                <li className="hover:text-[#2CB742] hover:translate-x-1 transition-all duration-200">
                  <Link to="/why" className="text-inherit no-underline block">Why ChefCo</Link>
                </li>
              </ul>
            </div>

            {/* SERVICES */}
            <div className="justify-self-end md:justify-self-start">
              <h4 className="font-black text-xs uppercase tracking-widest mb-3 text-[#2CB742]">Account</h4>
              <ul className="space-y-2 text-white/70 text-xs font-semibold m-0 p-0 list-none">
                <li className="hover:text-[#2CB742] hover:translate-x-1 transition-all duration-200">
                  <Link to="/profile" className="text-inherit no-underline block">My Profile</Link>
                </li>
                <li className="hover:text-[#2CB742] hover:translate-x-1 transition-all duration-200">
                  <Link to="/my-lists" className="text-inherit no-underline block">My Lists</Link>
                </li>
                <li className="hover:text-[#2CB742] hover:translate-x-1 transition-all duration-200">
                  <Link to="/checkout" className="text-inherit no-underline block">Checkout</Link>
                </li>
                <li className="hover:text-[#2CB742] hover:translate-x-1 transition-all duration-200">
                  <Link to="/inventory" className="text-inherit no-underline block">Inventory</Link>
                </li>
              </ul>
            </div>
          </div>


          {/* COLUMN 4: NEWSLETTER (Col span 3) */}
          <div className="md:col-span-3">
            <h4 className="font-black text-xs uppercase tracking-widest mb-2 text-[#2CB742]">Wholesale Updates</h4>
            <p className="text-white/60 mb-3 leading-snug text-xs">Get supplier updates and exclusive bulk pricing offers.</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="email"
                  placeholder="Email address"
                  className="bg-black/40 border border-white/15 rounded-xl px-3.5 py-2 text-xs outline-none w-full text-white placeholder-white/35 focus:border-[#2CB742] transition shadow-inner"
                />
              </div>
              <button
                className="bg-[#2CB742] hover:bg-[#239b36] transition px-4 py-2 rounded-xl font-black text-xs text-black whitespace-nowrap border-none cursor-pointer shadow-md flex items-center gap-1 shrink-0"
              >
                <span>Join</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <p className="text-white/35 text-[10px] mt-2 font-medium">No spam. Unsubscribe anytime.</p>
          </div>

        </div>

        {/* BOTTOM BAR: COMPACT COPYRIGHT & LEGAL LINKS */}
        <div className="mt-5 flex flex-col sm:flex-row items-center justify-between text-[11px] text-white/45 font-medium gap-3">
          <p className="m-0">© {new Date().getFullYear()} CHEFCO Hospitality Partners LLP. All rights reserved.</p>
          <div className="flex gap-5">
            <a href="/" className="hover:text-[#2CB742] transition text-inherit no-underline">Privacy Policy</a>
            <a href="/" className="hover:text-[#2CB742] transition text-inherit no-underline">Terms of Service</a>
            <a href="/" className="hover:text-[#2CB742] transition text-inherit no-underline">Support</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;